import type { CorrectionDraft, FieldDescriptor, JsonValue, ImportedJobSummary } from './types';

export interface LocationChoice {
  country: string;
  city: string;
  remote: boolean;
}
export interface LocationOption {
  id: string;
  label: string;
}
export const locationPaths = [
  'CountryCode',
  'CountryId',
  'CountryName',
  'CityId',
  'CityName',
  'IsRemote',
  'HasMultipleLocations',
  'MunicipalityName',
  'Address',
  'PostalCode',
  'PostArea',
  'PostCountryName',
  'VisitingAddress',
  'VisitingArea',
  'GooglePlaceId',
  'WorkplacePhone',
];

export function locationDrafts(choice: LocationChoice): Record<string, CorrectionDraft> {
  const drafts: Record<string, CorrectionDraft> = {};
  locationPaths.forEach((path) => {
    drafts[path] = { mode: 'reset', text: '' };
  });
  drafts.IsRemote = { mode: 'value', text: String(choice.remote) };
  drafts.HasMultipleLocations = { mode: 'value', text: 'false' };
  if (!choice.remote) {
    drafts.CountryCode = { mode: 'value', text: choice.country };
    if (choice.country === 'SE') {
      drafts.CityId = { mode: 'value', text: choice.city };
    }
  }
  return drafts;
}

export function validLocation(
  choice: LocationChoice,
  countries: LocationOption[],
  cities: LocationOption[]
): boolean {
  return (
    choice.remote ||
    (countries.some((country) => country.id === choice.country) &&
      (choice.country !== 'SE' || cities.some((city) => city.id === choice.city)))
  );
}

export function issueSection(path: string | null): string | null {
  if (!path) return null;
  if (locationPaths.includes(path)) return 'location';
  if (path.startsWith('Application')) return 'application';
  if (['SourceStartsAt', 'SourceEndsAt', 'SourceDurationDays'].includes(path)) return 'publication';
  if (
    ['Title', 'Description', 'Seats', 'WorkplaceName', 'WorkplaceHomePage', 'AdUrl'].includes(path)
  )
    return 'content';
  if (path.startsWith('Contacts.')) return 'contacts';
  if (
    path.startsWith('Terms.') ||
    ['ExperienceRequired', 'DetailsEmploymentType', 'ProfessionName'].includes(path)
  )
    return 'employment';
  return null;
}

export function editorGroups(
  fields: FieldDescriptor[]
): { key: string; fields: FieldDescriptor[] }[] {
  const contentOrder = [
    'Title',
    'WorkplaceName',
    'Description',
    'Seats',
    'AdUrl',
    'WorkplaceHomePage',
  ];
  const rank = (path: string) => {
    const index = contentOrder.indexOf(path);
    return index < 0 ? contentOrder.length : index;
  };

  return ['content', 'application', 'publication', 'employment', 'contacts']
    .map((key) => ({
      key,
      fields: fields.filter(
        (field) =>
          field.source === 'effective' &&
          field.editable &&
          field.valueType !== 'object' &&
          field.valueType !== 'array' &&
          issueSection(field.path) === key
      ),
    }))
    .filter((group) => group.fields.length > 0)
    .map((group) => ({
      ...group,
      fields:
        group.key === 'content'
          ? group.fields.sort((left, right) => rank(left.path) - rank(right.path))
          : group.fields,
    }));
}

export function localDateValue(value: JsonValue | undefined): string {
  if (typeof value !== 'string' || value.startsWith('0001-')) return '';
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return '';
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
}

export function dateCorrection(value: string, original: JsonValue | undefined): string {
  if (value && value === localDateValue(original) && typeof original === 'string') return original;
  const date = new Date(value);
  return Number.isFinite(date.getTime()) ? date.toISOString() : value;
}

export function attentionReason(item: ImportedJobSummary): string {
  // The server knows which issues block publication; a missing salesperson alone does not.
  if (item.attentionReason !== undefined) return item.attentionReason || '';
  if (item.sourceState !== 'available') return '';

  if (item.reviewRequired) return 'review-changes';
  if (item.evaluationState === 'configuration-error') return 'fix-configuration';
  if (item.evaluationState === 'incomplete') return 'correct-information';
  if (item.processingState === 'failed') return 'retry-processing';

  if (item.evaluationState === 'eligible') {
    if (item.issueCount > 0) return 'resolve-blocker';
    if (item.approvalState === 'pending') return 'approve';
  }

  return '';
}

export function reviewStatus(item: ImportedJobSummary): string {
  if (item.sourceState === 'withdrawn' || item.sourceState === 'expired') {
    return item.sourceState;
  }

  if (item.reviewRequired) return 'review-required';

  if (['incomplete', 'excluded', 'configuration-error'].includes(item.evaluationState)) {
    return item.evaluationState;
  }

  if (item.processingState === 'failed') return 'failed';
  if (item.approvalState === 'pending') return 'approval-pending';

  return item.processingState === 'pending' ? item.evaluationState : item.processingState;
}
